import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Check, Loader2, Package, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Dialog, DialogFooter } from "@/components/ui/library/dialog";
import useServices from "@/features/videoProduction/api/useServices";
import usePrograms from "@/features/academy/api/usePrograms";
import useCurrentIndustry from "@/hooks/globalapis/useCurrentIndustry";
import { useAttachVariant } from "@/features/leads/api/usePaymentMutations";
import { useChildMutations } from "@/features/leads/api/useChildMutations";
import { refreshAfterDealCreate } from "@/module/utils/refreshAfterDealCreate";
import { dealNameFor, variantFinancials } from "./attachVariant.plan";
import { isSellable, SEAT_TONE_CLASS, seatsShortLabel, seatsTone, type VariantSeats } from "@/features/academy/utils/variantSeats";

interface AttachServiceModalProps {
  open: boolean;
  onClose: () => void;
  /** The deal to attach to — omitted in create mode (see `createForLead`). */
  dealId?: number | null;
  dealName: string;
  /**
   * Deals only on attach: the lead has no deal yet, so confirming CREATES its
   * first deal carrying the picked variant (`POST /core/childs`, started at
   * the lead's own stage) instead of patching an existing deal.
   */
  createForLead?: { id: number; name?: string | null } | null;
  /**
   * CHANGE mode (the Overview card's "Edit"): the deal already carries a
   * variant. The dialog opens on it, and saving swaps it — the server replaces
   * the deal's link for this connector, keeps every payment already recorded
   * and re-derives the pending amount. The deal's discount is kept (a
   * percentage re-applies to the new price).
   */
  current?: {
    variantId: number;
    discountType?: string | null;
    discountValue?: number | null;
    discountAmount?: number | null;
  } | null;
}

const fmt = (n: number) => n.toLocaleString("en-IN");

type Variant = {
  id: number;
  name?: string | null;
  price?: number | null;
  courseFees?: number | null;
  costAllowed?: number | null;
  /** Academy batches: live seat count from the server; absent on other modules. */
  seats?: VariantSeats | null;
  schedule?: string | null;
};
type Item = { id: number; name?: string | null; title?: string | null; variants?: Variant[] };
const nameOf = (v: { name?: string | null; title?: string | null } | null | undefined) =>
  String(v?.name ?? v?.title ?? "").toLowerCase();

export default function AttachServiceModal({
  open,
  onClose,
  dealId,
  dealName,
  createForLead,
  current,
}: AttachServiceModalProps) {
  const { industry } = useCurrentIndustry();
  const isAcademy = industry?.id === 5;

  const { services, isLoading: servicesLoading } = useServices();
  const { programs, isLoading: programsLoading } = usePrograms();

  const items = useMemo(() => {
    if (isAcademy) return programs ?? [];
    return services ?? [];
  }, [isAcademy, programs, services]);

  const isLoading = isAcademy ? programsLoading : servicesLoading;
  const nicheModelName = isAcademy
    ? "academyProgramVariant"
    : "videoServiceVarients";
  const itemLabel = isAcademy ? "Program" : "Service";

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);
  const [search, setSearch] = useState("");

  // Search matches the program / service name OR any of its variants; a
  // variant hit keeps its parent in the list and opens it.
  const q = search.trim().toLowerCase();
  const filteredItems = useMemo<Item[]>(() => {
    const list = (items ?? []) as Item[];
    if (!q) return list;
    return list.filter((item) => nameOf(item).includes(q) || (item.variants ?? []).some((v) => nameOf(v).includes(q)));
  }, [items, q]);
  const isOpenItem = (item: Item) =>
    expandedId === item.id || (q !== "" && (item.variants ?? []).some((v) => nameOf(v).includes(q)));

  const attachVariant = useAttachVariant();
  const { createChild } = useChildMutations();
  const isCreate = !!createForLead?.id;
  const isChange = !isCreate && !!current?.variantId;
  const isPending = attachVariant.isPending || createChild.isPending;
  const parentOf = (variantId: number) =>
    (items as Item[]).find((it) => (it.variants ?? []).some((v) => v.id === variantId));

  // CHANGE mode opens on the deal's current variant (its program expanded),
  // once per opening — a refetch of the list never undoes the user's pick.
  const seeded = useRef(false);
  useEffect(() => {
    if (!open) {
      seeded.current = false;
      return;
    }
    if (seeded.current || !isChange || !current) return;
    const parent = parentOf(current.variantId);
    const variant = parent?.variants?.find((v) => v.id === current.variantId);
    if (!parent || !variant) return;
    seeded.current = true;
    setExpandedId(parent.id);
    setSelectedVariant(variant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isChange, current?.variantId, items]);

  const unchanged = isChange && selectedVariant?.id === current?.variantId;

  const handleConfirm = () => {
    if (!selectedVariant || unchanged) return;
    const price = selectedVariant.price ?? selectedVariant.courseFees ?? 0;
    const cost = selectedVariant.costAllowed ?? 0;
    const totalDealValue = price + cost;
    const moduleScopes = [{ nicheModelName, nicheItemId: selectedVariant.id }];
    // A change keeps the deal's discount (see `variantFinancials`).
    const financials = variantFinancials(totalDealValue, isChange ? current ?? null : null);
    // "<Program> - <Variant>" / "<Service> - <Variant>" — the server mints
    // the same on create when the name is missing; sent explicitly so the
    // request is self-describing. A change renames the deal to follow its
    // variant, as a course change does (server `ChildUpdate`).
    const name = dealNameFor(parentOf(selectedVariant.id), selectedVariant);

    if (isCreate) {
      // First deal, born with the variant attached. `startAtLeadStage` puts it
      // where the auto-created deal would have sat (the lead's Choices stage).
      const leadId = Number(createForLead!.id);
      createChild.mutate(
        {
          leadId,
          ...(name ? { name } : {}),
          moduleScopes,
          financials,
          startAtLeadStage: true,
        },
        {
          onSuccess: () => {
            refreshAfterDealCreate(leadId);
            onClose();
          },
        },
      );
      return;
    }

    attachVariant.mutate(
      {
        childId: Number(dealId),
        moduleScopes,
        financials,
        ...(isChange && name ? { name } : {}),
        ...(isChange ? { successMessage: `${itemLabel} changed` } : {}),
      },
      { onSuccess: handleClose }
    );
  };

  const handleClose = () => {
    setExpandedId(null);
    setSelectedVariant(null);
    setSearch("");
    onClose();
  };

  const selectedPrice = selectedVariant ? (selectedVariant.price ?? selectedVariant.courseFees ?? 0) : 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      {/* Xale modal language (the Attach Course dialog): title + description
          in the header, a search box over the list, Cancel + primary in the
          footer. */}
      <DialogContent className="w-[min(96vw,560px)] sm:max-w-[560px] rounded-[24px] p-0 gap-0 max-h-[min(88vh,680px)] flex flex-col overflow-hidden">
        <DialogHeader className="px-4 pt-[11px] pb-[11px] border-b border-[#e6e8e7]">
          <DialogTitle className="text-[18px] font-medium text-black tracking-[-0.72px] leading-normal">
            {isChange ? "Change" : "Attach"} {itemLabel}
          </DialogTitle>
          <DialogDescription className="text-[#505e59] text-[13px] tracking-[-0.52px] leading-[1.35] mt-0.5">
            {isCreate ? `${dealName} · creates this lead's first deal` : dealName}
          </DialogDescription>
        </DialogHeader>

        <div className="px-4 pt-3 pb-2 flex flex-col gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-[#9ba3a0]" />
            <input
              type="text"
              value={search}
              autoFocus
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search by ${itemLabel.toLowerCase()} or variant name`}
              aria-label={`Search ${itemLabel.toLowerCase()}s`}
              className="w-full h-9 pl-8 pr-3 text-[13px] rounded-lg border border-[#e6e8e7] bg-[#f7f9f8] text-[#1e302a] placeholder:text-[#b3b8b5] focus:outline-none focus:border-[var(--color-success)] focus:ring-1 focus:ring-[var(--color-success)]/20 transition-colors"
            />
          </div>
          <span className="text-[12px] text-[#697571]">
            {itemLabel}s ({filteredItems.length}{q && items.length !== filteredItems.length ? ` of ${items.length}` : ""})
          </span>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="size-5 animate-spin text-[#9ba3a0]" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="size-8 text-[#c5c9c7] mb-2" />
              <p className="text-[13px] text-[#8a918d]">
                {q ? `Nothing matches “${search.trim()}”` : `No ${itemLabel.toLowerCase()}s found`}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {filteredItems.map((item) => {
                const variants = item.variants ?? [];
                const isExpanded = isOpenItem(item);
                const shownVariants = q
                  ? variants.filter((v) => nameOf(item).includes(q) || nameOf(v).includes(q))
                  : variants;

                return (
                  <div key={item.id} className="rounded-xl border border-[#e6e8e7] overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : item.id)}
                      className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left hover:bg-[#f7f9f8] transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronDown className="size-3.5 text-[#9ba3a0] shrink-0" />
                      ) : (
                        <ChevronRight className="size-3.5 text-[#9ba3a0] shrink-0" />
                      )}
                      <span className="text-[13px] font-semibold text-[#1e302a] truncate flex-1">
                        {item.name ?? item.title ?? "Unnamed"}
                      </span>
                      <span className="text-[11px] text-[#9ba3a0] shrink-0">
                        {variants.length} variant{variants.length !== 1 ? "s" : ""}
                      </span>
                    </button>

                    {isExpanded && shownVariants.length > 0 && (
                      <div className="border-t border-[#f0f1f0] bg-[#fafbfa]">
                        {shownVariants.map((v) => {
                          const price = v.price ?? v.courseFees ?? 0;
                          const isSelected = selectedVariant?.id === v.id;
                          // A full or closed batch cannot be sold: the row says so and stays unclickable.
                          const sellable = isSellable(v.seats);
                          const seatText = seatsShortLabel(v.seats);
                          return (
                            <button
                              key={v.id}
                              type="button"
                              disabled={!sellable}
                              title={!sellable ? (v.seats?.status === "closed" ? "Closed for sales" : "This batch is full") : v.schedule ?? undefined}
                              onClick={() => setSelectedVariant(isSelected ? null : v)}
                              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left transition-colors ${
                                isSelected
                                  ? "bg-[#f0faf5] border-l-2 border-l-[#267355]"
                                  : "hover:bg-[#f0f1f0] border-l-2 border-l-transparent"
                              } ${!sellable ? "cursor-not-allowed opacity-60 hover:bg-transparent" : ""}`}
                            >
                              <div
                                className={`size-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                                  isSelected ? "border-[#267355] bg-[#267355]" : "border-[#c5c9c7]"
                                }`}
                              >
                                {isSelected && <Check className="size-2.5 text-white" strokeWidth={3} />}
                              </div>
                              <span
                                className={`flex-1 min-w-0 text-[13px] truncate ${
                                  isSelected ? "font-semibold text-[#267355]" : "text-[#374741]"
                                }`}
                              >
                                {v.name ?? "Unnamed Variant"}
                                {v.schedule && <span className="ml-1.5 text-[11px] font-normal text-[#9ba3a0]">{v.schedule}</span>}
                              </span>
                              {seatText && (
                                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-medium ${SEAT_TONE_CLASS[seatsTone(v.seats)]}`}>
                                  {seatText}
                                </span>
                              )}
                              <span
                                className={`text-[13px] tabular-nums shrink-0 ${
                                  isSelected ? "font-bold text-[#267355]" : "font-medium text-[#697571]"
                                }`}
                              >
                                ₹{fmt(price)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {isExpanded && shownVariants.length === 0 && (
                      <div className="px-4 py-3 border-t border-[#f0f1f0] bg-[#fafbfa]">
                        <p className="text-[12px] text-[#9ba3a0] italic">No variants available</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[#e6e8e7] bg-[#fafbfa] sm:justify-between">
          <div className="min-w-0 text-[12px] text-[#697571]">
            {selectedVariant ? (
              <span className="truncate block">
                {unchanged ? "Current" : "Selected"}: <strong className="text-[#1e302a]">{selectedVariant.name}</strong>
                {" · "}₹{fmt(selectedPrice)}
              </span>
            ) : (
              <span className="text-[#9ba3a0]">
                {isChange ? "Select the new variant" : "Select a variant to attach"}
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="cancel" type="button" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleConfirm}
              disabled={!selectedVariant || unchanged || isPending}
            >
              {isChange
                ? isPending ? "Saving…" : "Save"
                : isPending ? "Attaching…" : isCreate ? "Attach & create deal" : "Attach"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
